import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp, MapPin, FileText, Users, Briefcase, Compass, UserPlus, Truck, Check, ChevronDown } from "lucide-react";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";
import { useT } from "@/i18n/helpers";
import Seo from "@/components/Seo";

const Section = ({ children, className = "", id }) => (
 <section id={id} className={`border-t border-nanda-line ${className}`}>
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28">{children}</div>
 </section>
);
const Overline = ({ children }) => (<div className="label-overline text-nanda-muted mb-4">{children}</div>);

export default function ConseilImplantation() {
 const tt = useT();

 const SERVICES = [
 { i: TrendingUp, t: tt("Études de marché", "Market studies", "Estudos de mercado"),
 d: tt("Analyse du secteur, de la concurrence, des opportunités et des risques.", "Analysis of the sector, competition, opportunities and risks.", "Análise do sector, da concorrência, das oportunidades e dos riscos.") },
 { i: MapPin, t: tt("Recherche de terrains et de locaux", "Land and premises search", "Procura de terrenos e instalações"),
 d: tt("Identification de sites adaptés aux besoins du projet.", "Identification of sites tailored to the project's needs.", "Identificação de locais adaptados às necessidades do projecto.") },
 { i: FileText, t: tt("Accompagnement administratif", "Administrative support", "Apoio administrativo"),
 d: tt("Information sur les démarches, formalités et procédures d'implantation.", "Guidance on establishment steps, formalities and procedures.", "Informação sobre os passos, formalidades e procedimentos de implantação.") },
 { i: Users, t: tt("Recherche de partenaires locaux", "Local partner search", "Procura de parceiros locais"),
 d: tt("Fournisseurs, distributeurs, prestataires et partenaires de confiance.", "Suppliers, distributors, service providers and trusted partners.", "Fornecedores, distribuidores, prestadores e parceiros de confiança.") },
 { i: Briefcase, t: tt("Développement commercial", "Business development", "Desenvolvimento comercial"),
 d: tt("Lancement, stratégie commerciale et développement du réseau.", "Launch, commercial strategy and network development.", "Lançamento, estratégia comercial e desenvolvimento da rede.") },
 { i: Compass, t: tt("Gestion de projet", "Project management", "Gestão de projecto"),
 d: tt("Coordination et suivi des étapes d'implantation.", "Coordination and follow-up of the establishment stages.", "Coordenação e acompanhamento das etapas de implantação.") },
 { i: UserPlus, t: tt("Recrutement", "Recruitment", "Recrutamento"),
 d: tt("Organisation des équipes et besoins en ressources humaines.", "Team organisation and human resource needs.", "Organização das equipas e necessidades de recursos humanos.") },
 { i: Truck, t: tt("Logistique", "Logistics", "Logística"),
 d: tt("Approvisionnement, transport et stockage.", "Sourcing, transport and storage.", "Abastecimento, transporte e armazenamento.") },
 ];

 const SECTEURS = [
 tt("Énergies renouvelables", "Renewable energy", "Energias renováveis"),
 tt("Commerce & distribution", "Trade & distribution", "Comércio e distribuição"),
 tt("Agroalimentaire", "Food industry", "Agro-alimentar"),
 tt("Agriculture", "Agriculture", "Agricultura"),
 tt("Industrie légère", "Light industry", "Indústria ligeira"),
 tt("Immobilier", "Real estate", "Imobiliário"),
 tt("Tourisme", "Tourism", "Turismo"),
 tt("Artisanat", "Craftsmanship", "Artesanato"),
 tt("Santé", "Healthcare", "Saúde"),
 tt("Éducation", "Education", "Educação"),
 tt("Hôtellerie & restauration", "Hospitality & catering", "Hotelaria e restauração"),
 ];

 const METHODE = [
 tt("Analyse des besoins", "Needs analysis", "Análise das necessidades"),
 tt("Étude de faisabilité", "Feasibility study", "Estudo de viabilidade"),
 tt("Identification des opportunités", "Opportunity identification", "Identificação de oportunidades"),
 tt("Stratégie d'implantation", "Establishment strategy", "Estratégia de implantação"),
 tt("Mise en relation acteurs locaux", "Introduction to local players", "Mise en relation com actores locais"),
 tt("Accompagnement opérationnel", "Operational support", "Acompanhamento operacional"),
 tt("Suivi du projet", "Project follow-up", "Acompanhamento do projecto"),
 ];

 const POURQUOI = [
 tt("Expertise du terrain", "Field expertise", "Especialidade de terreno"),
 tt("Réseau de partenaires", "Partner network", "Rede de parceiros"),
 tt("Accompagnement personnalisé", "Personalised support", "Acompanhamento personalizado"),
 tt("Vision à long terme", "Long-term vision", "Visão de longo prazo"),
 tt("Connaissance des réalités locales", "Knowledge of local realities", "Conhecimento das realidades locais"),
 tt("Approche pragmatique", "Pragmatic approach", "Abordagem pragmática"),
 tt("Suivi de proximité", "Close follow-up", "Acompanhamento de proximidade"),
 tt("Solutions adaptées", "Tailored solutions", "Soluções adaptadas"),
 ];

 const PUBLICS = [
 tt("Entreprises", "Companies", "Empresas"),
 tt("Investisseurs", "Investors", "Investidores"),
 "PME", tt("Grandes entreprises", "Large companies", "Grandes empresas"),
 "ONG", tt("Fondations", "Foundations", "Fundações"),
 tt("Institutions", "Institutions", "Instituições"),
 tt("Collectivités", "Local authorities", "Autarquias"),
 tt("Porteurs de projets", "Project leaders", "Promotores de projectos"),
 tt("Diaspora", "Diaspora", "Diáspora"),
 ];

 const LIVRABLES = [
 { t: tt("Note de cadrage", "Framing note", "Nota de enquadramento"),
 d: tt("Périmètre, hypothèses, objectifs et livrables attendus, validés avec vous.", "Scope, assumptions, objectives and expected deliverables, validated with you.", "Perímetro, hipóteses, objectivos e entregáveis esperados, validados consigo.") },
 { t: tt("Étude de marché", "Market study", "Estudo de mercado"),
 d: tt("Analyse sectorielle, concurrents, opportunités, risques et recommandations chiffrées.", "Sector analysis, competitors, opportunities, risks and quantified recommendations.", "Análise sectorial, concorrentes, oportunidades, riscos e recomendações quantificadas.") },
 { t: tt("Cahier des charges administratif", "Administrative specifications", "Caderno de encargos administrativo"),
 d: tt("Formalités, délais et interlocuteurs identifiés étape par étape.", "Formalities, deadlines and interlocutors identified step by step.", "Formalidades, prazos e interlocutores identificados passo a passo.") },
 { t: tt("Plan de déploiement", "Deployment plan", "Plano de implementação"),
 d: tt("Feuille de route opérationnelle : sourcing, recrutement, logistique, ouverture.", "Operational roadmap: sourcing, recruitment, logistics, opening.", "Roteiro operacional: sourcing, recrutamento, logística, abertura.") },
 { t: tt("Rapports d'avancement", "Progress reports", "Relatórios de progresso"),
 d: tt("Points réguliers et remontée factuelle de terrain, en français, portugais ou anglais.", "Regular updates and factual field reporting, in French, Portuguese or English.", "Pontos regulares e reporte factual do terreno, em francês, português ou inglês.") },
 { t: tt("Réseau de partenaires locaux", "Local partner network", "Rede de parceiros locais"),
 d: tt("Fournisseurs, distributeurs, avocats, comptables, transitaires de confiance.", "Suppliers, distributors, lawyers, accountants, trusted freight forwarders.", "Fornecedores, distribuidores, advogados, contabilistas, transitários de confiança.") },
 ];

 return (
 <div data-testid="page-conseil-implantation">
 <Seo />
 <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden bg-nanda-ink">
 <img src="/photos/home-hero.jpg" alt={tt("Paysage économique de la Guinée-Bissau, architecture contemporaine ouest-africaine", "Economic landscape of Guinea-Bissau, contemporary West African architecture", "Paisagem económica da Guiné-Bissau, arquitectura contemporânea oeste-africana")} className="absolute inset-0 h-full w-full object-cover opacity-70" loading="eager" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/40 via-nanda-ink/25 to-nanda-ink/75" />
 <div className="relative z-10 h-full mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-24 md:pb-32">
 <FadeUp>
 <div className="label-overline text-white/85 mb-6">NANDA SÁ Conseil</div>
 <h1 className="font-serif text-white text-5xl md:text-7xl lg:text-[88px] leading-[1.02] max-w-5xl">
 {tt("Votre partenaire pour réussir", "Your partner to succeed in", "O seu parceiro para ter sucesso")}<br />
 <span className="text-nanda-gold italic">{tt("votre implantation au Sénégal et en Guinée-Bissau.", "your establishment in Senegal and Guinea-Bissau.", "na sua implantação no Senegal e na Guiné-Bissau.")}</span>
 </h1>
 <p className="mt-8 max-w-2xl text-white/85 text-lg md:text-xl leading-relaxed">
 {tt("Nous accompagnons les entreprises, les investisseurs et les organisations à chaque étape, de l'étude de faisabilité à la mise en œuvre opérationnelle.",
 "We support companies, investors and organisations at every step, from feasibility study to operational implementation.",
 "Acompanhamos as empresas, os investidores e as organizações em cada etapa, do estudo de viabilidade à implementação operacional.")}
 </p>
 <div className="mt-12 flex flex-wrap gap-4">
 <Link to="/contact" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-8 py-4 label-overline hover:bg-nanda-gold hover:text-white transition-colors duration-500" data-testid="conseil-cta-accompagnement">
 {tt("Demander un accompagnement", "Request support", "Solicitar acompanhamento")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 <a href="#services" className="inline-flex items-center gap-3 border border-white/50 text-white px-8 py-4 label-overline hover:bg-white hover:text-nanda-ink transition-colors duration-500" data-testid="conseil-cta-services">
 {tt("Découvrir nos services", "Discover our services", "Descobrir os nossos serviços")} <ChevronDown size={16} strokeWidth={1.5} />
 </a>
 </div>
 </FadeUp>
 </div>
 </section>

 <Section>
 <div className="grid grid-cols-1 md:grid-cols-12 gap-14">
 <FadeUp className="md:col-span-5">
 <Overline>{tt("Notre expertise", "Our expertise", "A nossa especialidade")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Un partenaire local qui connaît le terrain.", "A local partner who knows the ground.", "Um parceiro local que conhece o terreno.")}
 </h2>
 </FadeUp>
 <FadeUp delay={0.15} className="md:col-span-7 text-nanda-inkSoft text-[17px] leading-relaxed space-y-5">
 <p>{tt("Nous connaissons les réalités économiques, administratives et culturelles du Sénégal et de la Guinée-Bissau. Chaque projet est étudié individuellement, avec une solution adaptée à vos objectifs.",
 "We understand the economic, administrative and cultural realities of Senegal and Guinea-Bissau. Every project is studied individually, with a solution tailored to your objectives.",
 "Conhecemos as realidades económicas, administrativas e culturais do Senegal e da Guiné-Bissau. Cada projecto é estudado individualmente, com uma solução adaptada aos seus objectivos.")}</p>
 <p>{tt("De l'étude préalable à l'installation opérationnelle, nous restons à vos côtés dans la durée.",
 "From the preliminary study to operational setup, we stay by your side over time.",
 "Do estudo prévio à instalação operacional, permanecemos ao seu lado no longo prazo.")}</p>
 </FadeUp>
 </div>
 </Section>

 <Section className="bg-nanda-bgAlt" id="services">
 <FadeUp className="mb-14 max-w-3xl">
 <Overline>{tt("Nos services", "Our services", "Os nossos serviços")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Huit expertises pour une implantation réussie.", "Eight areas of expertise for a successful establishment.", "Oito especialidades para uma implantação bem-sucedida.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {SERVICES.map((s) => (
 <StaggerItem key={s.t} className="bg-white p-8 md:p-10">
 <s.i size={28} strokeWidth={1.2} className="text-nanda-gold mb-5" />
 <div className="font-serif text-2xl text-nanda-ink leading-tight">{s.t}</div>
 <p className="mt-4 text-sm text-nanda-inkSoft leading-relaxed">{s.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Secteurs d'intervention", "Areas of intervention", "Sectores de intervenção")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Onze secteurs, une même exigence.", "Eleven sectors, one same standard.", "Onze sectores, uma mesma exigência.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-nanda-line border border-nanda-line">
 {SECTEURS.map((s) => (<div key={s} className="bg-white p-6 md:p-8"><div className="font-serif text-lg md:text-xl text-nanda-ink">{s}</div></div>))}
 </div>
 </Section>

 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Notre méthode", "Our method", "O nosso método")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Sept étapes, un projet réussi.", "Seven steps, one successful project.", "Sete etapas, um projecto bem-sucedido.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-nanda-line border border-nanda-line">
 {METHODE.map((step, i) => (
 <StaggerItem key={step} className="bg-white p-6 md:p-8">
 <div className="font-serif text-nanda-gold text-2xl mb-3">0{i + 1}</div>
 <div className="font-serif text-base md:text-lg text-nanda-ink leading-snug">{step}</div>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section>
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Pourquoi choisir NANDA SÁ Conseil", "Why choose NANDA SÁ Consulting", "Porquê escolher a NANDA SÁ Consultoria")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Huit atouts, un même objectif : votre réussite.", "Eight strengths, one same goal: your success.", "Oito vantagens, um mesmo objectivo: o seu sucesso.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6">
 {POURQUOI.map((t) => (
 <div key={t} className="flex items-start gap-3 py-3 border-t border-nanda-line">
 <Check size={16} strokeWidth={2} className="text-nanda-gold mt-1 shrink-0" />
 <span className="text-sm text-nanda-ink leading-snug">{t}</span>
 </div>
 ))}
 </div>
 </Section>

 <Section className="bg-nanda-bgAlt">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("À qui s'adresse ce service", "Who this service is for", "A quem se destina este serviço")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Un accompagnement pour chaque profil.", "Support for every profile.", "Um acompanhamento para cada perfil.")}
 </h2>
 </FadeUp>
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-nanda-line border border-nanda-line">
 {PUBLICS.map((p) => (<div key={p} className="bg-white p-6 md:p-7 text-center"><div className="font-serif text-lg md:text-xl text-nanda-ink">{p}</div></div>))}
 </div>
 </Section>

 <Section id="livrables">
 <FadeUp className="mb-12 max-w-3xl">
 <Overline>{tt("Livrables & garanties", "Deliverables & guarantees", "Entregáveis e garantias")}</Overline>
 <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
 {tt("Six livrables clairs.", "Six clear deliverables.", "Seis entregáveis claros.")}
 </h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-nanda-line border border-nanda-line">
 {LIVRABLES.map((li) => (
 <StaggerItem key={li.t} className="bg-white p-8 md:p-10">
 <div className="font-serif text-xl md:text-2xl text-nanda-ink leading-tight">{li.t}</div>
 <p className="mt-3 text-sm text-nanda-inkSoft leading-relaxed">{li.d}</p>
 </StaggerItem>
 ))}
 </Stagger>
 </Section>

 <Section className="bg-nanda-terracotta text-white">
 <FadeUp className="max-w-4xl mx-auto text-center">
 <Overline><span className="text-[#F6E7D7]">{tt("Confidentialité & professionnalisme", "Confidentiality & professionalism", "Confidencialidade e profissionalismo")}</span></Overline>
 <h3 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.08] mt-4">
 {tt("Transformez votre projet d'implantation", "Turn your establishment project", "Transforme o seu projecto de implantação")}<br />
 <span className="text-[#F6E7D7] italic">{tt("en réussite durable.", "into lasting success.", "em sucesso duradouro.")}</span>
 </h3>
 <p className="mt-10 text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
 {tt("Parlez-nous de votre projet. Nous revenons vers vous avec une première évaluation et une proposition d'accompagnement adaptée.",
 "Tell us about your project. We come back to you with an initial assessment and a tailored support proposal.",
 "Fale-nos do seu projecto. Voltaremos com uma primeira avaliação e uma proposta de acompanhamento adaptada.")}
 </p>
 <div className="mt-12">
 <Link to="/contact" className="inline-flex items-center gap-3 bg-white text-nanda-ink px-10 py-5 label-overline hover:bg-nanda-sand transition-colors duration-500" data-testid="conseil-cta-final">
 {tt("Être accompagné", "Get supported", "Ser acompanhado")} <ArrowUpRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </FadeUp>
 </Section>
 </div>
 );
}
